import React, { useState, useEffect } from 'react';
import { KTCard, toAbsoluteUrl } from '../../../../_metronic/helpers';

/**
 * DistributorCard Component
 * Displays individual distributor information in a card format
 */
const DistributorCard = ({ distributor, onClick }) => {
    return (
        <div
            className="card shadow-sm mb-4 cursor-pointer hover-elevate-up"
            style={{
                border: "1px solid #eaeaea",
                transition: "all 0.3s ease",
                borderRadius: "12px"
            }}
            onClick={() => onClick(distributor)}
        >
            <div className="card-body p-4">
                <div className="d-flex align-items-center">
                    {/* Profile Image Section */}
                    <div className="symbol symbol-60px symbol-circle me-4">
                        <img
                            src={distributor?.profile_image || toAbsoluteUrl('/media/avatars/300-1.jpg')}
                            alt={distributor?.name}
                            className="symbol-label"
                            style={{
                                objectFit: 'cover',
                                border: '2px solid #f1f1f1'
                            }}
                        />
                    </div>

                    {/* Distributor Information */}
                    <div className="flex-grow-1">
                        <div className="d-flex flex-column">
                            {/* Name */}
                            <div className="fw-bold fs-5 text-dark mb-1">
                                {distributor?.name || 'N/A'}
                            </div>

                            {/* Firm Name */}
                            <div className="fw-semibold fs-6 text-primary mb-1">
                                <i className="bi bi-building me-2"></i>
                                {distributor?.firm_name || 'N/A'}
                            </div>

                            {/* Code */}
                            <div className="fw-semibold fs-7 text-muted mb-1">
                                <i className="bi bi-hash me-2"></i>
                                Code: {distributor?.code || 'N/A'}
                            </div>

                            {/* Mobile Number */}
                            <div className="fw-semibold fs-7 text-success">
                                <i className="bi bi-phone me-2"></i>
                                {distributor?.mobile_number || 'N/A'}
                            </div>
                        </div>
                    </div>

                    {/* Action Icon */}
                    <div className="d-flex align-items-center">
                        <i className="fas fs-6 fa-chevron-right text-gray-600"></i>
                        {/* <div className="symbol symbol-35px symbol-circle">
                            <span
                                className="symbol-label fs-6 fw-semibold text-white"
                                style={{ backgroundColor: "#1e40af" }}
                            >
                                <i className="fas fa-chevron-right text-white"></i>
                            </span>
                        </div> */}
                    </div>
                </div>

                {/* Additional Info Row */}
                <div className="d-flex justify-content-between align-items-center mt-3 pt-3"
                    style={{ borderTop: "1px solid #f1f1f1" }}>
                    <div className="d-flex align-items-center">
                        <span className="badge badge-light-success fs-8 fw-semibold">
                            Active
                        </span>
                    </div>
                    <div className="text-muted fs-8">
                        Click to view details
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * DistributorListHeader Component
 * Displays the header section with title and search functionality
 */
const DistributorListHeader = ({ onSearch, searchTerm, totalCount }) => {
    return (
        <div className="d-flex flex-column mb-8">
            {/* Title Section */}
            <div className="d-flex justify-content-between align-items-center mb-6">
                <div>
                    <h1 className="fw-bold fs-2 text-dark mb-2">Distributor List</h1>
                    <p className="text-muted fs-6 mb-0">
                        Manage and view all distributors in your network
                    </p>
                </div>
                <div className="d-flex align-items-center">
                    <span className="badge badge-light-primary fs-6 fw-semibold px-4 py-2">
                        Total: {totalCount}
                    </span>
                </div>
            </div>

            {/* Search Section */}
            <div className="d-flex flex-wrap gap-4 align-items-center">
                <div className="position-relative flex-grow-1" style={{ maxWidth: "400px" }}>
                    <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                    <input
                        type="text"
                        className="form-control form-control-solid ps-10"
                        placeholder="Search by name, firm, code, or mobile..."
                        value={searchTerm}
                        onChange={(e) => onSearch(e.target.value)}
                        style={{ borderRadius: "8px" }}
                    />
                </div>
                {/* <button className="btn btn-primary btn-sm">
                    <i className="bi bi-funnel me-2"></i>
                    Filter
                </button> */}
                <button className="btn btn-light btn-sm">
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh
                </button>
            </div>
        </div>
    );
};

/**
 * EmptyState Component
 * Displays when no distributors are found
 */
const EmptyState = ({ isSearching }) => {
    return (
        <div className="text-center py-10">
            <img
                src={toAbsoluteUrl('/media/illustrations/sketchy-1/5.png')}
                alt="No distributors"
                className="h-200px mb-5"
            />
            <h3 className="fw-bold text-dark mb-4">
                {isSearching ? 'No distributors found' : 'No distributors available'}
            </h3>
            <p className="text-muted fs-6 mb-6">
                {isSearching
                    ? 'Try adjusting your search criteria or clear the search to see all distributors.'
                    : 'There are currently no distributors in the system. Add some distributors to get started.'
                }
            </p>
            {!isSearching && (
                <button className="btn btn-primary">
                    <i className="bi bi-plus-circle me-2"></i>
                    Add Distributor
                </button>
            )}
        </div>
    );
};

/**
 * LoadingState Component
 * Displays loading skeleton while data is being fetched
 */
const LoadingState = () => {
    return (
        <div className="d-flex flex-column gap-4">
            {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="card shadow-sm">
                    <div className="card-body p-4">
                        <div className="d-flex align-items-center">
                            <div className="symbol symbol-60px symbol-circle me-4">
                                <div className="symbol-label bg-light">
                                    <div className="spinner-border spinner-border-sm text-muted"></div>
                                </div>
                            </div>
                            <div className="flex-grow-1">
                                <div className="placeholder-glow">
                                    <div className="placeholder col-6 mb-2"></div>
                                    <div className="placeholder col-4 mb-2"></div>
                                    <div className="placeholder col-5 mb-2"></div>
                                    <div className="placeholder col-3"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

/**
 * DistributorList Component
 * Main component that displays the list of distributors with search and filtering
 */
const DistributorList = () => {

    const [distributors, setDistributors] = useState([]);
    const [filteredDistributors, setFilteredDistributors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Sample data - Replace with actual API call
    const sampleDistributors = [
        {
            id: 1,
            name: "Rajesh Kumar",
            firm_name: "Kumar Distributors Pvt Ltd",
            code: "KD001",
            mobile_number: "+91 9876543210",
            profile_image: null,
            status: "active",
            location: "Mumbai, Maharashtra"
        },
        {
            id: 2,
            name: "Priya Sharma",
            firm_name: "Sharma Trading Company",
            code: "ST002",
            mobile_number: "+91 9876543211",
            profile_image: null,
            status: "active",
            location: "Delhi, Delhi"
        },
        {
            id: 3,
            name: "Amit Patel",
            firm_name: "Patel Enterprises",
            code: "PE003",
            mobile_number: "+91 9876543212",
            profile_image: null,
            status: "active",
            location: "Ahmedabad, Gujarat"
        },
        {
            id: 4,
            name: "Sunita Gupta",
            firm_name: "Gupta Distribution Network",
            code: "GD004",
            mobile_number: "+91 9876543213",
            profile_image: null,
            status: "active",
            location: "Pune, Maharashtra"
        },
        {
            id: 5,
            name: "Vikram Singh",
            firm_name: "Singh Trading House",
            code: "ST005",
            mobile_number: "+91 9876543214",
            profile_image: null,
            status: "active",
            location: "Jaipur, Rajasthan"
        },
        {
            id: 6,
            name: "Meera Reddy",
            firm_name: "Reddy Distributors",
            code: "RD006",
            mobile_number: "+91 9876543215",
            profile_image: null,
            status: "active",
            location: "Hyderabad, Telangana"
        }
    ];

    // Simulate API call
    useEffect(() => {
        const fetchDistributors = async () => {
            try {
                setLoading(true);
                // Simulate API delay
                await new Promise(resolve => setTimeout(resolve, 1500));

                // Replace this with actual API call
                // const response = await fetch('/api/distributors');
                // const data = await response.json();

                setDistributors(sampleDistributors);
                setFilteredDistributors(sampleDistributors);
                setError(null);
            } catch (err) {
                setError('Failed to fetch distributors. Please try again.');
                console.error('Error fetching distributors:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchDistributors();
    }, []);

    // Handle search functionality
    const handleSearch = (term) => {
        setSearchTerm(term);

        if (!term.trim()) {
            setFilteredDistributors(distributors);
            return;
        }

        const filtered = distributors.filter(distributor =>
            distributor.name.toLowerCase().includes(term.toLowerCase()) ||
            distributor.firm_name.toLowerCase().includes(term.toLowerCase()) ||
            distributor.code.toLowerCase().includes(term.toLowerCase()) ||
            distributor.mobile_number.includes(term)
        );

        setFilteredDistributors(filtered);
    };

    // Handle distributor card click
    const handleDistributorClick = (distributor) => {
        console.log('Distributor clicked:', distributor);

        // Navigate to distributor details page or perform action
        // navigate(`/distributor/${distributor.id}`, { state: { distributor } });

        // For now, just show an alert
        // alert(`Selected: ${distributor.name} from ${distributor.firm_name}`);
    };

    // Error state
    if (error) {
        return (
            <KTCard>
                <div className="text-center py-10">
                    <img
                        src={toAbsoluteUrl('/media/illustrations/sketchy-1/18.png')}
                        alt="Error"
                        className="h-200px mb-5"
                    />
                    <h3 className="fw-bold text-danger mb-4">Something went wrong</h3>
                    <p className="text-muted fs-6 mb-6">{error}</p>
                    <button
                        className="btn btn-primary"
                        onClick={() => window.location.reload()}
                    >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Try Again
                    </button>
                </div>
            </KTCard>
        );
    }

    return (
        <div className="d-flex flex-column">
            <KTCard>
                <div className="card-body px-4">
                    <DistributorListHeader
                        onSearch={handleSearch}
                        searchTerm={searchTerm}
                        totalCount={distributors.length}
                    />

                    {loading ? (
                        <LoadingState />
                    ) : filteredDistributors.length === 0 ? (
                        <EmptyState isSearching={searchTerm.length > 0} />
                    ) : (
                        <div className="row">
                            {filteredDistributors.map((distributor) => (
                                <div key={distributor.id} className="col-12 col-md-6 col-xl-4">
                                    <DistributorCard
                                        distributor={distributor}
                                        onClick={handleDistributorClick}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </KTCard>

            {/* Floating Action Button */}
            {/* <div className="position-fixed bottom-0 end-0 me-5 mb-5" style={{ zIndex: 1000 }}>
                <button
                    className="btn btn-primary btn-lg rounded-circle shadow-lg"
                    style={{ width: "60px", height: "60px" }}
                    title="Add New Distributor"
                >
                    <i className="bi bi-plus fs-2"></i>
                </button>
            </div> */}
        </div>
    );
};

export default DistributorList;
